import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Upload, 
  FileSpreadsheet, 
  FileText,
  Download, 
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Search,
  Check,
  X,
  Database,
  Globe,
  Edit2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_FORMATS = ['.xlsx', '.xls', '.docx', '.doc', '.pdf'];

export default function ExcelFill() {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [analysisPreview, setAnalysisPreview] = useState(null);
  const [webMatchesState, setWebMatchesState] = useState([]);
  const [validatingPrices, setValidatingPrices] = useState(false);

  const isValidFile = (filename) => {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
    return SUPPORTED_FORMATS.includes(ext);
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
    if (['.xlsx', '.xls'].includes(ext)) return FileSpreadsheet;
    return FileText;
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile.name)) {
      setFile(droppedFile);
      setResult(null);
      setWebMatchesState([]);
      analyzeFile(droppedFile);
    } else {
      toast.error(`Formats acceptés : ${SUPPORTED_FORMATS.join(', ')}`);
    }
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (isValidFile(selectedFile.name)) {
        setFile(selectedFile);
        setResult(null);
        setWebMatchesState([]);
        analyzeFile(selectedFile);
      } else {
        toast.error(`Formats acceptés : ${SUPPORTED_FORMATS.join(', ')}`);
      }
    }
  };

  const analyzeFile = async (fileToAnalyze) => {
    const formData = new FormData();
    formData.append('file', fileToAnalyze);

    try {
      const response = await axios.post(
        `${API}/documents/analyze`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setAnalysisPreview(response.data);
    } catch (error) {
      console.error("Analysis error:", error);
      setAnalysisPreview({ error: error.response?.data?.detail || "Erreur d'analyse" });
    }
  };

  const handleAutoFill = async () => {
    if (!file) {
      toast.error("Veuillez d'abord sélectionner un fichier");
      return;
    }

    setProcessing(true);
    setResult(null);
    setWebMatchesState([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/documents/auto-fill`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      setResult(response.data);
      
      // Initialize web matches state for editing
      if (response.data.web_matches?.length > 0) {
        setWebMatchesState(response.data.web_matches.map(m => ({
          ...m,
          edited_price: m.price,
          validated: false,
          editing: false
        })));
      }
      
      const totalFilled = response.data.filled_count || 0;
      toast.success(`${totalFilled} prix remplis automatiquement !`);
      
      if (response.data.web_matches_count > 0) {
        toast.info(`${response.data.web_matches_count} prix trouvés par recherche web - validation recommandée`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du remplissage");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.output_filename) return;

    try {
      const response = await axios.get(
        `${API}/documents/download/${result.output_filename}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.output_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Fichier téléchargé !");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handlePriceEdit = (index, value) => {
    setWebMatchesState(prev => prev.map((item, i) => 
      i === index ? { ...item, edited_price: parseFloat(value) || 0 } : item
    ));
  };

  const toggleEditing = (index) => {
    setWebMatchesState(prev => prev.map((item, i) => 
      i === index ? { ...item, editing: !item.editing } : item
    ));
  };

  const handleValidatePrice = async (index) => {
    const item = webMatchesState[index];
    
    try {
      await axios.post(
        `${API}/pricing/validate`,
        {
          description: item.description,
          quantity: 1,
          unit: "pce",
          unit_price: item.edited_price
        },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      
      setWebMatchesState(prev => prev.map((it, i) => 
        i === index ? { ...it, validated: true, editing: false } : it
      ));
      
      toast.success("Prix validé et ajouté à la base");
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const handleValidateAll = async () => {
    const unvalidated = webMatchesState.filter(m => !m.validated);
    if (unvalidated.length === 0) {
      toast.info("Tous les prix sont déjà validés");
      return;
    }

    setValidatingPrices(true);

    try {
      await axios.post(
        `${API}/pricing/validate-batch`,
        unvalidated.map(item => ({
          description: item.description,
          quantity: 1,
          unit: "pce",
          unit_price: item.edited_price
        })),
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      
      setWebMatchesState(prev => prev.map(it => ({ ...it, validated: true, editing: false })));
      toast.success(`${unvalidated.length} prix validés et ajoutés à la base`);
    } catch (error) {
      toast.error("Erreur lors de la validation");
    } finally {
      setValidatingPrices(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setAnalysisPreview(null);
    setWebMatchesState([]);
  };

  const FileIcon = file ? getFileIcon(file.name) : FileSpreadsheet;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Chiffrage automatique
          </h1>
          <p className="text-gray-500 mt-1">
            Uploadez un document de soumission, les prix seront remplis automatiquement
          </p>
        </div>

        {/* Upload zone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#1E8B5A]" />
              Document de soumission
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!file ? (
              <div
                className={`
                  border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
                  ${dragActive ? 'border-[#1E8B5A] bg-green-50' : 'border-gray-200 hover:border-[#1E8B5A] hover:bg-green-50/50'}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('doc-input').click()}
                data-testid="upload-zone"
              >
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg text-gray-600 mb-2">
                  Glissez votre fichier ici
                </p>
                <p className="text-sm text-gray-400">
                  ou cliquez pour parcourir
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Formats : Excel (.xlsx, .xls), Word (.docx, .doc), PDF
                </p>
                <input
                  id="doc-input"
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.docx,.doc,.pdf"
                  onChange={handleFileChange}
                  data-testid="doc-file-input"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* File info */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <FileIcon className="h-8 w-8 text-[#1E8B5A]" />
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetForm} data-testid="change-file-btn">
                    Changer
                  </Button>
                </div>

                {/* Analysis preview */}
                {analysisPreview && !analysisPreview.error && (
                  <div className="p-4 rounded-lg bg-gray-50 border">
                    <p className="text-sm font-medium text-gray-700 mb-2">Analyse du fichier :</p>
                    <div className="text-sm">
                      <span className="text-gray-500">Cellules à remplir :</span>
                      <span className="ml-2 font-bold text-[#1E8B5A]">
                        {analysisPreview.analysis?.total_fillable_cells || 0}
                      </span>
                    </div>
                    {analysisPreview.analysis?.sheets?.filter(s => s.fillable_count > 0).map((sheet, idx) => (
                      <div key={idx} className="mt-1 text-xs text-gray-500">
                        • {sheet.name} : {sheet.fillable_count} postes
                      </div>
                    ))}
                    {analysisPreview.analysis?.tables?.filter(t => t.fillable_count > 0).map((table, idx) => (
                      <div key={idx} className="mt-1 text-xs text-gray-500">
                        • Tableau {table.index + 1} : {table.fillable_count} postes
                      </div>
                    ))}
                  </div>
                )}

                {/* Auto-fill button */}
                {!result && (
                  <Button 
                    onClick={handleAutoFill}
                    disabled={processing}
                    className="w-full h-14 text-lg bg-[#1E8B5A] hover:bg-[#166A43]"
                    data-testid="auto-fill-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        <span className="animate-pulse">Recherche des prix...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5 mr-2" />
                        Remplir automatiquement
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Processing animation */}
        {processing && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="py-8">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <Search className="h-12 w-12 text-blue-500 animate-pulse" />
                  <div className="absolute -right-1 -bottom-1">
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-lg font-medium text-blue-700">Recherche des prix en cours...</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Interrogation de la base de données et recherche web parallélisée
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <>
            {/* Summary Card */}
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1E8B5A]">
                  <CheckCircle className="h-5 w-5" />
                  Chiffrage terminé
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-white border text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Database className="h-4 w-4 text-[#1E8B5A]" />
                      <span className="text-xs text-gray-500">Base de données</span>
                    </div>
                    <p className="text-2xl font-bold text-[#1E8B5A]">{result.db_matches_count || 0}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white border text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Globe className="h-4 w-4 text-blue-500" />
                      <span className="text-xs text-gray-500">Recherche web</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-500">{result.web_matches_count || 0}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white border text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span className="text-xs text-gray-500">Non trouvés</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-500">{result.unfilled_count || 0}</p>
                  </div>
                </div>

                {result.note && (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
                    {result.note}
                  </div>
                )}

                <Button 
                  onClick={handleDownload}
                  className="w-full h-12 text-lg"
                  data-testid="download-btn"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Télécharger le fichier rempli
                </Button>
              </CardContent>
            </Card>

            {/* Web Matches Validation */}
            {webMatchesState.length > 0 && (
              <Card className="border-blue-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-blue-700">
                      <Globe className="h-5 w-5" />
                      Prix trouvés par recherche web
                      <span className="text-sm font-normal text-gray-500">
                        ({webMatchesState.filter(m => !m.validated).length} à valider)
                      </span>
                    </CardTitle>
                    {webMatchesState.some(m => !m.validated) && (
                      <Button 
                        onClick={handleValidateAll}
                        disabled={validatingPrices}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="validate-all-btn"
                      >
                        {validatingPrices ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        Tout valider
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Ces prix ont été estimés via recherche web. Vérifiez et ajustez si nécessaire, puis validez pour enrichir votre base de données.
                  </p>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {webMatchesState.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border transition-colors ${
                          item.validated 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                        data-testid={`web-match-${idx}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate" title={item.description}>
                              {item.description}
                            </p>
                            {item.price_min && item.price_max && (
                              <p className="text-xs text-gray-500 mt-1">
                                Fourchette : {item.price_min} - {item.price_max} CHF
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                Confiance: {Math.round((item.confidence || 0) * 100)}%
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {item.editing ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.edited_price}
                                  onChange={(e) => handlePriceEdit(idx, e.target.value)}
                                  className="w-24 h-8 text-right"
                                  data-testid={`price-input-${idx}`}
                                />
                                <span className="text-sm text-gray-500">CHF</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleEditing(idx)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-gray-900">
                                  {item.edited_price.toFixed(2)} CHF
                                </span>
                                {!item.validated && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleEditing(idx)}
                                    className="h-8 w-8 p-0"
                                    data-testid={`edit-btn-${idx}`}
                                  >
                                    <Edit2 className="h-4 w-4 text-gray-400" />
                                  </Button>
                                )}
                              </div>
                            )}
                            
                            {item.validated ? (
                              <span className="flex items-center gap-1 text-green-600 text-sm">
                                <CheckCircle className="h-4 w-4" />
                                Validé
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleValidatePrice(idx)}
                                className="bg-[#1E8B5A] hover:bg-[#166A43]"
                                data-testid={`validate-btn-${idx}`}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Valider
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Database matches */}
            {result.db_matches?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1E8B5A]">
                    <Database className="h-5 w-5" />
                    Prix trouvés dans la base de données
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {result.db_matches.map((match, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate">{match.description}</p>
                          <p className="text-xs text-gray-400">{match.method}</p>
                        </div>
                        <span className="text-sm font-medium text-[#1E8B5A] ml-4">
                          {match.price.toFixed(2)} CHF
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Not found items */}
            {result.not_found?.length > 0 && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-5 w-5" />
                    Éléments non trouvés ({result.not_found.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    Ces éléments n&apos;ont pas pu être chiffrés automatiquement.
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.not_found.map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-700 py-1">
                        • {item.description}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* New document button */}
            <Button 
              variant="outline"
              onClick={resetForm}
              className="w-full"
              data-testid="new-document-btn"
            >
              Traiter un autre fichier
            </Button>
          </>
        )}

        {/* Info */}
        {!result && (
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Comment ça fonctionne :</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-600">
                  <li>Le système analyse la structure de votre document</li>
                  <li>Il recherche d&apos;abord les prix déjà stockés dans la base de données</li>
                  <li>Lorsqu&apos;il ne trouve pas le prix d&apos;un élément, il effectue des recherches multiples sur le web</li>
                  <li>En éditant et en validant les prix, vous enrichissez automatiquement votre base de données</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
